import * as CdsIcons from '@central-design-system/icons';
import { computed } from 'vue';

import { paramCase, propsFactory } from '@components/utils';

import type { TCdsIconsName } from '@central-design-system/icons';
import type { EventProp } from '@components/utils';
import type { ComputedRef, PropType } from 'vue';

export type TFieldTypes = typeof allowedFieldType[number];

export interface IFieldProps {
  active: boolean;
  autofocus: boolean;
  prefix: string | undefined;
  suffix: string | undefined;
  prependInnerIcon: TCdsIconsName | undefined;
  appendInnerIcon: TCdsIconsName | undefined;
  type: TFieldTypes;
  role: string | undefined;
  placeholder: string | undefined;
  counter: boolean;
  disabled: boolean;
  limit: number | undefined;
  clearable: boolean;
  clearLabel: string;
  persistentClearable: boolean;
  'onClick:appendInner': EventProp | undefined;
  'onClick:prependInner': EventProp | undefined;
}
export interface IUseField {
  hasPrependInnerIcon: ComputedRef<boolean>;
  hasAppendInnerIcon: ComputedRef<boolean>;
  fieldClasses: ComputedRef<Record<string, boolean>>;
  fieldProps: string[];
  handleClickPrependInner: (event: MouseEvent) => void;
  handleClickAppendInner: (event: MouseEvent) => void;
}

export const allowedFieldType = ['text', 'password', 'email', 'number', 'tel', 'url'] as const;
export const makeFieldProps = propsFactory(
  {
    /**
     * Активный элемент
     */
    active: {
      type: Boolean as PropType<boolean>,
      default: false
    },
    /**
     * Автофокус
     */
    autofocus: {
      type: Boolean as PropType<boolean>,
      default: false
    },
    /**
     * Добавить строку внутрь поля перед значением
     */
    prefix: {
      type: String as PropType<string>,
      default: undefined
    },
    /**
     * Добавить строку внутрь поля после значения
     */
    suffix: {
      type: String as PropType<string>,
      default: undefined
    },
    /**
     * Иконка внутри элемента перед значением поля
     */
    prependInnerIcon: {
      type: String as PropType<TCdsIconsName>,
      default: undefined,
      validator: (value: TCdsIconsName) => CdsIcons.allowedIcons.includes(value)
    },
    /**
     * Иконка внутри элемента после значения поля
     */
    appendInnerIcon: {
      type: String as PropType<TCdsIconsName>,
      default: undefined,
      validator: (value: TCdsIconsName) => CdsIcons.allowedIcons.includes(value)
    },
    /**
     * Тип поля
     */
    type: {
      type: String as PropType<TFieldTypes>,
      default: 'text',
      validator: (value: TFieldTypes) => allowedFieldType.includes(value)
    },
    /**
     * Роль поля
     */
    role: {
      type: String as PropType<string>,
      default: undefined
    },
    /**
     * Плейсхолдер поля
     */
    placeholder: {
      type: String as PropType<string>,
      default: undefined
    },
    /**
     * Счетчик введенных символов
     */
    counter: {
      type: Boolean as PropType<boolean>,
      default: false
    },
    /**
     * Ограничение ввода по количеству символов
     */
    limit: {
      type: [Number, String] as PropType<number>,
      default: undefined
    },
    /**
     * Очищаемое поле
     */
    clearable: {
      type: Boolean as PropType<boolean>,
      default: false
    },
    /**
     * Лейбл для кнопки очистки поля ввода
     */
    clearLabel: {
      type: String as PropType<string>,
      default: 'Очистить поле ввода'
    },
    /**
     * Всегда показывать иконку очистки поля
     */
    persistentClearable: {
      type: Boolean as PropType<boolean>,
      default: false
    },
    /**
     * Событие клика по иконке внутри элемента перед значением поля
     */
    'onClick:prependInner': {
      type: Function as PropType<EventProp>,
      default: undefined
    },
    /**
     * Событие клика по иконке внутри элемента после значения поля
     */
    'onClick:appendInner': {
      type: Function as PropType<EventProp>,
      default: undefined
    }
  },
  'field'
);

export function useField(props: IFieldProps, slots: Record<string, unknown>, name: string): IUseField {
  const fieldProps = Object.keys(makeFieldProps());

  const hasPrependInnerIcon = computed((): boolean => !!props.prependInnerIcon || !!slots['prepend-inner']);
  const hasAppendInnerIcon = computed((): boolean => !!props.appendInnerIcon || !!slots['append-inner']);

  const fieldClasses = computed(() => ({
    [`${paramCase(name)}--has-prepend-inner`]: hasPrependInnerIcon.value,
    [`${paramCase(name)}--has-append-inner`]: hasAppendInnerIcon.value,
    [`${paramCase(name)}--has-prepend-inner-clickable`]: !!props['onClick:prependInner'],
    [`${paramCase(name)}--has-append-inner-clickable`]: !!props['onClick:appendInner']
  }));

  const handleClickPrependInner = () => {
    if (props.disabled) return;
    props['onClick:prependInner']?.() as (event: MouseEvent) => void;
  };
  const handleClickAppendInner = () => {
    if (props.disabled) return;
    props['onClick:appendInner']?.() as (event: MouseEvent) => void;
  };

  return {
    hasPrependInnerIcon,
    hasAppendInnerIcon,
    fieldClasses,
    fieldProps,
    handleClickPrependInner,
    handleClickAppendInner
  };
}
